"use client";

import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";

export default function ProWhipHeader({ data }: any) {
    const baseUrl = process.env.NEXT_PUBLIC_PROWHIP_BASE_URL;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [langOpen, setLangOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [expandedSubIndex, setExpandedSubIndex] = useState<number | null>(null);

    const menuItems = data?.advancedMenu || [];
    const logo = data?.Logo?.media;
    const { enableLanguageSwitcher } = data || {};

    const localeParam = searchParams.get("locale") || "en_US";

    /* SWITCH LANGUAGE */
    const switchLanguage = (lang: "en" | "kr") => {
        const newLocale = lang === "kr" ? "ko_KR" : "en_US";

        const url = new URL(window.location.href);
        url.searchParams.delete("locale");
        url.searchParams.set("locale", newLocale);

        router.push(url.pathname + url.search);
        setLangOpen(false);
        setMobileOpen(false);
    };

    /* ADD LOCALE TO LINKS */
    const withLocale = (url: string) => {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);

        params.delete("locale");
        params.set("locale", localeParam);

        return `${url}?${params.toString()}`;
    };

    const resolveUrl = (item: any) => {
        if (item?.url) return item.url;
        if (item?.page?.pageFolder?._url) {
            return item.page.pageFolder._url;
        }
        return "#";
    };

    const isNewTab = (item: any) => {
        return (
            item?.openInNewTab === true ||
            item?.page?.pageFolder?.configuration?.openInNewTab === true
        );
    };

    const getLabel = (item: any) => {
        const label = item?.label;
        if (!label) return "";

        if (typeof label === "object") {
            return (
                label[localeParam] ||
                label["en_US"] ||
                Object.values(label)[0] ||
                ""
            );
        }
        return label;
    };

    return (
        <>
            {/* Black top spacing bar */}
            <div
                style={{
                    position: "fixed",
                    top: "20px",
                    left: 0,
                    width: "100%",
                    height: "90px",
                    backgroundColor: "#000",
                    zIndex: 10000,
                }}
            />

            <header
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "72px",
                    zIndex: 10001,
                    backgroundColor: "#000",
                }}
            >
                <div className="mx-auto max-w-[1440px] flex items-center justify-between px-6 lg:px-16 h-full text-white pt-10">

                    {/* Hamburger (Mobile + iPad) */}
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="lg:hidden text-white text-2xl"
                    >
                        ☰
                    </button>

                    {/* Logo */}
                    <Link
                        href={
                            baseUrl
                                ? `${baseUrl}?locale=${localeParam}`
                                : `/?locale=${localeParam}`
                        }
                    >
                        <span className="flex items-center cursor-pointer">
                            {logo && (
                                <img
                                    src={logo.file}
                                    alt="Logo"
                                    className="h-10 lg:h-12 w-auto"
                                />
                            )}
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-8 text-lg font-small ml-16 px-6">
                        {menuItems.map((item: any, index: number) => {
                            const level1 = (item?.childrenLevel1 || []).filter(
                                (c: any) => c?.page
                            );
                            const hasSubmenu = level1.length > 0;
                            const newTab = isNewTab(item);

                            return (
                                <div key={index} className="relative group">
                                    <Link
                                        href={withLocale(resolveUrl(item))}
                                        target={newTab ? "_blank" : undefined}
                                        rel={newTab ? "noopener noreferrer" : undefined}
                                    >
                                        <span className="cursor-pointer flex items-center gap-1 py-2">
                                            {getLabel(item)}
                                            {hasSubmenu && (
                                                <span className="text-[10px] leading-none ml-1">
                                                    ▼
                                                </span>
                                            )}
                                        </span>
                                    </Link>

                                    {hasSubmenu && (
                                        <div className="absolute left-0 top-full hidden group-hover:block bg-white text-black min-w-[220px] shadow-lg border">
                                            {level1.map((child: any, i: number) => {
                                                const level2 = (child?.childrenLevel2 || []).filter(
                                                    (c: any) => c?.page
                                                );
                                                const hasLevel2 = level2.length > 0;
                                                const childNewTab = isNewTab(child);

                                                return (
                                                    <div key={i} className="relative group">
                                                        <Link
                                                            href={withLocale(resolveUrl(child))}
                                                            target={childNewTab ? "_blank" : undefined}
                                                            rel={childNewTab ? "noopener noreferrer" : undefined}
                                                        >
                                                            <span style={{ color: "#000" }} className="block px-5 py-3 hover:bg-gray-100 whitespace-nowrap flex items-center justify-between">
                                                                {getLabel(child)}
                                                                {hasLevel2 && (
                                                                    <span className="text-[10px] ml-2">▶</span>
                                                                )}
                                                            </span>
                                                        </Link>

                                                        {hasLevel2 && (
                                                            <div className="absolute left-full top-0 hidden group-hover:block bg-white text-black min-w-[220px] shadow-lg border">
                                                                {level2.map((sub: any, j: number) => {
                                                                    const subNewTab = isNewTab(sub);

                                                                    return (
                                                                        <Link
                                                                            key={j}
                                                                            href={withLocale(resolveUrl(sub))}
                                                                            target={subNewTab ? "_blank" : undefined}
                                                                            rel={subNewTab ? "noopener noreferrer" : undefined}
                                                                        >
                                                                            <span className="block px-5 py-3 hover:bg-gray-100 whitespace-nowrap">
                                                                                {getLabel(sub)}
                                                                            </span>
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Desktop Language Switcher */}
                        {enableLanguageSwitcher && (
                            <div className="relative ml-6">
                                <button
                                    onClick={() => setLangOpen(!langOpen)}
                                    className="flex items-center gap-1"
                                >
                                    {localeParam === "ko_KR" ? "한국어" : "English"}
                                    <span className="text-[10px] ml-1">▼</span>
                                </button>

                                {langOpen && (
                                    <div className="absolute right-0 top-full mt-2 bg-white text-black min-w-[140px] shadow-lg border">
                                        <button style={{ color: "#000" }}
                                            onClick={() => switchLanguage("en")}
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                        >
                                            English
                                        </button>
                                        <button style={{ color: "#000" }}
                                            onClick={() => switchLanguage("kr")}
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                        >
                                            한국어
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                </div>
            </header>

            {/* Overlay (Mobile + iPad) */}
            {mobileOpen && (
                <div
                    onClick={() => setMobileOpen(false)}
                    className="fixed inset-0 bg-black/50 z-[10002] lg:hidden"
                />
            )}

            {/* Drawer (Mobile + iPad) */}
            <div
                className={`fixed top-0 left-0 h-full w-[85%] max-w-[380px] bg-[#666666] z-[10003] lg:hidden
                transform transition-transform duration-300 ease-in-out
                ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#7a7a7a]">
                    <span className="text-white text-lg font-semibold uppercase">
                        Menu
                    </span>
                    <button onClick={() => setMobileOpen(false)} className="text-white">
                        ✕
                    </button>
                </div>

                <ul className="px-6 py-4 text-white text-lg font-medium overflow-y-auto">
                    {menuItems.map((item: any, index: number) => {
                        const level1 = (item?.childrenLevel1 || []).filter(
                            (c: any) => c?.page
                        );
                        const hasSubmenu = level1.length > 0;

                        return (
                            <li key={index} className="border-b border-[#7a7a7a] py-4">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() =>
                                        hasSubmenu
                                            ? setExpandedIndex(
                                                expandedIndex === index ? null : index
                                            )
                                            : router.push(withLocale(resolveUrl(item)))
                                    }
                                >
                                    <span>{getLabel(item)}</span>
                                    {hasSubmenu && (
                                        <span
                                            className={`transition-transform ${expandedIndex === index ? "rotate-180" : ""
                                                }`}
                                        >
                                            ▼
                                        </span>
                                    )}
                                </div>

                                {hasSubmenu && expandedIndex === index && (
                                    <ul className="mt-3 space-y-3 pl-4 text-base text-gray-200">
                                        {level1.map((child: any, i: number) => (
                                            <li
                                                key={i}
                                                onClick={() => {
                                                    router.push(withLocale(resolveUrl(child)));
                                                    setMobileOpen(false);
                                                }}
                                                className="cursor-pointer hover:text-white"
                                            >
                                                {getLabel(child)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {enableLanguageSwitcher && (
                    <div className="px-6 py-6 border-t border-[#7a7a7a]">
                        <div className="flex gap-6 text-white text-sm">
                            <button onClick={() => switchLanguage("en")}>English</button>
                            <button onClick={() => switchLanguage("kr")}>한국어</button>
                        </div>
                    </div>
                )}
            </div>

            <div className="h-[72px]" />
        </>
    );
}
